import { ChangeDetectionStrategy, Component, OnInit, QueryList, ViewChildren } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatIconModule } from '@angular/material/icon';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatTooltip, MatTooltipModule } from '@angular/material/tooltip';
import { ActivatedRoute } from '@angular/router';
import { AssignRoomData, FeatureUpdatePayload, InstallationFeaturesStore } from '@store';
import { DeviceIds, FeatureActionFields, FeatureCommands, FeatureNames } from '@vi-iot/client';
import { Observable, switchMap, tap } from 'rxjs';
import { CommonStandaloneModule } from 'src/app/shared/common-standalone.module';
import { matTooltipSetter } from 'src/app/shared/functions/mat-tooltip-setter/mat-tooltip-setter';
import { ReferenceRoomSettingsService } from '../service/reference-room-settings.service';

@Component({
  selector: 'app-reference-room-settings-assign-room',
  standalone: true,
  imports: [
    CommonStandaloneModule,
    MatCheckboxModule,
    MatButtonModule,
    MatIconModule,
    MatTooltipModule,
    FormsModule,
    MatSlideToggleModule,
    MatIconModule,
    MatTooltipModule,
  ],
  templateUrl: './reference-room-settings-assign-room.component.html',
  styleUrl: './reference-room-settings-assign-room.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ReferenceRoomSettingsAssignRoomComponent implements OnInit {
  @ViewChildren(MatTooltip)
  set matTooltip(queryList: QueryList<MatTooltip>) {
    matTooltipSetter(queryList);
  }

  rooms$: Observable<AssignRoomData[]>;
  updatedRoomsData: AssignRoomData[];

  private gatewaySerial: string;
  private originalRoomsData: AssignRoomData[];
  private channelId: string;

  constructor(
    private installationFeaturesStore: InstallationFeaturesStore,
    private rrsService: ReferenceRoomSettingsService,
    private activatedRoute: ActivatedRoute
  ) {}

  ngOnInit(): void {
    this.mapRoomsData();
    this.gatewaySerial = this.activatedRoute.snapshot.queryParams['gatewaySerial'];
  }

  private mapRoomsData(): void {
    this.rooms$ = this.rrsService.getRoomsChannelsEntityDataForHeatingCircuit().pipe(
      switchMap(({ channelId }) => {
        if (!this.channelId) {
          this.channelId = channelId;
        }

        return this.installationFeaturesStore.getAssignRoomDataForChannel(channelId);
      }),
      tap((data) => {
        if (!this.originalRoomsData) {
          this.originalRoomsData = structuredClone(data);
          this.updatedRoomsData = structuredClone(data);
        }
      })
    );
  }

  updateRoomStatus(roomId: string): void {
    const matchingRoom = this.updatedRoomsData.find((room) => room.featureRoomId === roomId);
    matchingRoom.isSelected = !matchingRoom.isSelected;
  }

  saveChanges(): void {
    const featureUpdateData: FeatureUpdatePayload[] = this.updatedRoomsData
      .filter((room, index) => room.isSelected !== this.originalRoomsData[index].isSelected)
      .map(
        (room): FeatureUpdatePayload => {
          const commandName = room.isSelected
            ? FeatureCommands.addChannel
            : FeatureCommands.removeChannel;

          return {
            featureName: FeatureNames.roomDetails(room.featureRoomId),
            value: this.channelId,
            propertyName: '',
            paramName: FeatureActionFields.id,
            commandName,
            gatewaySerial: this.gatewaySerial,
            deviceId: DeviceIds.RoomControl,
          };
        }
      );

    this.installationFeaturesStore.updateMultipleFeatures(featureUpdateData);
  }
}
